import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import apiClient from '../../../api/client';
import { 
  Users, Search, Filter, Edit3, Trash2, ShieldCheck, UserCheck, 
  UserPlus, Mail, Calendar, Award, CheckCircle2, Download, RefreshCw, 
  Copy, Check, Shield, Lock, Power, AlertTriangle, Building2
} from 'lucide-react';
import { showSuccess, showError } from '../../../lib/alerts';

interface AdminUser {
  id: string;
  uid: string;
  email: string;
  fullName: string;
  displayName: string;
  avatarUrl?: string;
  role: string;
  roles?: string[];
  isPlatformAdmin: boolean;
  isActive: boolean;
  emailVerified: boolean;
  currentPlan: string;
  organizationId?: string;
  organizationName?: string;
  createdAt: string;
}

export default function UsersAdminView() {
  const queryClient = useQueryClient();
  const [searchTerm, setSearchTerm] = useState('');
  const [planFilter, setPlanFilter] = useState('ALL');
  const [roleFilter, setRoleFilter] = useState('ALL');
  const [copiedUid, setCopiedUid] = useState<string | null>(null);

  // Modal & Drawer State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isCreateMode, setIsCreateMode] = useState(false);
  const [selectedUser, setSelectedUser] = useState<AdminUser | null>(null);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [userDetails, setUserDetails] = useState<AdminUser | null>(null);

  const [formData, setFormData] = useState({
    fullName: '',
    displayName: '',
    email: '',
    password: '',
    role: 'Usuário',
    isPlatformAdmin: false,
    currentPlan: 'Pro',
    organizationId: '',
  });

  // Query Users
  const { data: users = [], isLoading, isError, refetch } = useQuery<AdminUser[]>({
    queryKey: ['admin-users', { search: searchTerm, role: roleFilter, plan: planFilter }],
    queryFn: async () => {
      const params: any = {};
      if (searchTerm) params.search = searchTerm;
      if (roleFilter !== 'ALL') params.role = roleFilter;
      if (planFilter !== 'ALL') params.plan = planFilter;
      const res = await apiClient.get('/api/admin/users', { params });
      const payload = res.data?.data ?? res.data;
      return Array.isArray(payload) ? payload : [];
    },
  });

  // Fetch Organizations for dropdown
  const { data: tenants = [] } = useQuery<any[]>({
    queryKey: ['admin-tenants-options'],
    queryFn: async () => {
      const res = await apiClient.get('/api/admin/tenants');
      const payload = res.data?.data ?? res.data;
      return Array.isArray(payload) ? payload : [];
    },
  });

  // Mutations
  const createMutation = useMutation({
    mutationFn: (data: typeof formData) => apiClient.post('/api/admin/users', data),
    onSuccess: (res) => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      setIsModalOpen(false);
      const tempPass = res.data?.generatedPassword;
      showSuccess(`Usuário criado com sucesso!${tempPass ? ` Senha provisória: ${tempPass}` : ''}`);
    },
    onError: (err: any) => {
      showError(err?.response?.data?.message || err.message || 'Erro ao criar usuário.');
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Partial<typeof formData> }) =>
      apiClient.put(`/api/admin/users/${id}`, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      setIsModalOpen(false);
      showSuccess('Usuário atualizado com sucesso!');
    },
    onError: (err: any) => {
      showError(err?.response?.data?.message || err.message || 'Erro ao atualizar usuário.');
    },
  });

  const deleteMutation = useMutation({
    mutationFn: (id: string) => apiClient.delete(`/api/admin/users/${id}`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      showSuccess('Usuário desativado com sucesso!');
    },
    onError: (err: any) => {
      showError(err?.response?.data?.message || err.message || 'Erro ao desativar usuário.');
    },
  });

  const toggleActiveMutation = useMutation({
    mutationFn: (id: string) => apiClient.post(`/api/admin/users/${id}/toggle-active`),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['admin-users'] });
      showSuccess('Status do usuário atualizado.');
    },
    onError: (err: any) => {
      showError(err?.response?.data?.message || err.message || 'Erro ao alterar status.');
    },
  });

  const resetPasswordMutation = useMutation({
    mutationFn: (id: string) => apiClient.post(`/api/admin/users/${id}/reset-password`),
    onSuccess: (res) => {
      const pass = res.data?.temporaryPassword;
      showSuccess(`Senha redefinida! Nova senha: ${pass || 'Verifique o e-mail'}`);
    },
    onError: (err: any) => {
      showError(err?.response?.data?.message || err.message || 'Erro ao redefinir senha.');
    },
  });

  const handleOpenCreate = () => {
    setIsCreateMode(true);
    setSelectedUser(null);
    setFormData({
      fullName: '',
      displayName: '',
      email: '',
      password: '',
      role: 'Usuário',
      isPlatformAdmin: false,
      currentPlan: 'Pro',
      organizationId: tenants[0]?.id || '',
    });
    setIsModalOpen(true);
  };

  const handleOpenEdit = (user: AdminUser) => {
    setIsCreateMode(false);
    setSelectedUser(user);
    setFormData({
      fullName: user.fullName || '',
      displayName: user.displayName || '',
      email: user.email || '',
      password: '',
      role: user.role || 'Usuário',
      isPlatformAdmin: !!user.isPlatformAdmin,
      currentPlan: user.currentPlan || 'Pro',
      organizationId: user.organizationId || '',
    });
    setIsModalOpen(true);
  };

  const handleOpenDetails = (user: AdminUser) => {
    setUserDetails(user);
    setIsDetailsOpen(true);
  };

  const handleDelete = (user: AdminUser) => {
    if (window.confirm(`Tem certeza que deseja desativar o usuário ${user.email}?`)) {
      deleteMutation.mutate(user.id);
    }
  };

  const handleCopyUid = (uid: string) => {
    navigator.clipboard.writeText(uid);
    setCopiedUid(uid);
    setTimeout(() => setCopiedUid(null), 2000);
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.email.trim()) return;

    if (isCreateMode) {
      createMutation.mutate(formData);
    } else if (selectedUser) {
      updateMutation.mutate({ id: selectedUser.id, data: formData });
    }
  };

  // Metrics
  const totalUsersCount = users.length;
  const platformAdminsCount = users.filter((u) => u.isPlatformAdmin).length;
  const activeUsersCount = users.filter((u) => u.isActive).length;
  const enterpriseCount = users.filter((u) => u.currentPlan?.toLowerCase() === 'enterprise').length;

  return (
    <div className="space-y-6 animate-in fade-in duration-500 max-w-[1400px] mx-auto pb-12">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4 mt-2">
        <div>
          <div className="flex items-center gap-2 text-xs font-semibold text-indigo-600 tracking-wider uppercase mb-1">
            <Users size={14} /> Gestão Global de Acessos
          </div>
          <h1 className="text-2xl font-bold text-gray-900 tracking-tight">
            Usuários & Credenciais
          </h1>
          <p className="text-xs text-gray-500 mt-0.5">
            Diretório corporativo unificado de usuários, papéis administrativos e permissões de segurança.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => refetch()}
            className="inline-flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 hover:bg-gray-50 text-gray-700 rounded-xl text-xs font-medium shadow-xs transition-all"
          >
            <RefreshCw size={13} className={isLoading ? 'animate-spin' : ''} /> Atualizar
          </button>
          <button
            onClick={handleOpenCreate}
            className="inline-flex items-center gap-2 px-4 py-2.5 bg-gray-900 hover:bg-black text-white rounded-xl text-xs font-semibold shadow-xs transition-all"
          >
            <UserPlus size={14} /> Novo Usuário
          </button>
        </div>
      </div>

      {/* Metrics Row */}
      <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
        <div className="bg-white p-4 rounded-2xl border border-gray-200/80 shadow-xs">
          <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Total de Usuários</p>
          <h3 className="text-2xl font-bold text-gray-900 mt-1">{totalUsersCount}</h3>
          <span className="text-[11px] text-gray-500 mt-1 block">Contas cadastradas</span>
        </div>
        <div className="bg-white p-4 rounded-2xl border border-gray-200/80 shadow-xs">
          <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Usuários Ativos</p>
          <h3 className="text-2xl font-bold text-emerald-600 mt-1">{activeUsersCount}</h3>
          <span className="text-[11px] text-emerald-600 font-medium mt-1 block">Acesso habilitado</span>
        </div>
        <div className="bg-white p-4 rounded-2xl border border-gray-200/80 shadow-xs">
          <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Platform Admins</p>
          <h3 className="text-2xl font-bold text-indigo-600 mt-1">{platformAdminsCount}</h3>
          <span className="text-[11px] text-indigo-600 font-medium mt-1 block">Acesso irrestrito</span>
        </div>
        <div className="bg-white p-4 rounded-2xl border border-gray-200/80 shadow-xs">
          <p className="text-[11px] font-semibold text-gray-400 uppercase tracking-wider">Plano Enterprise</p>
          <h3 className="text-2xl font-bold text-purple-600 mt-1">{enterpriseCount}</h3>
          <span className="text-[11px] text-purple-600 font-medium mt-1 block">Usuários VIP</span>
        </div>
      </div>

      {/* Filter & Search */}
      <div className="bg-white p-4 rounded-2xl border border-gray-200/80 shadow-xs flex flex-col md:flex-row items-center justify-between gap-4">
        <div className="relative w-full md:w-80">
          <Search size={15} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="text"
            placeholder="Buscar por nome ou e-mail..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-9 pr-4 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs focus:outline-hidden focus:bg-white focus:border-indigo-500 transition-all"
          />
        </div>

        <div className="flex flex-wrap items-center gap-3 w-full md:w-auto">
          <div className="flex items-center gap-2">
            <Filter size={14} className="text-gray-400" />
            <span className="text-xs font-semibold text-gray-600">Papel:</span>
            <select
              value={roleFilter}
              onChange={(e) => setRoleFilter(e.target.value)}
              className="px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-xl text-xs font-medium text-gray-700 focus:outline-hidden"
            >
              <option value="ALL">Todos os Papéis</option>
              <option value="ADMIN">Platform Admin</option>
              <option value="Usuário">Usuário</option>
            </select>
          </div>

          <div className="flex items-center gap-2">
            <span className="text-xs font-semibold text-gray-600">Plano:</span>
            <select
              value={planFilter}
              onChange={(e) => setPlanFilter(e.target.value)}
              className="px-3 py-1.5 bg-gray-50 border border-gray-200 rounded-xl text-xs font-medium text-gray-700 focus:outline-hidden"
            >
              <option value="ALL">Todos os Planos</option>
              <option value="Starter">Starter</option>
              <option value="Pro">Pro</option>
              <option value="Enterprise">Enterprise</option>
            </select>
          </div>
        </div>
      </div>

      {/* Users Table */}
      <div className="bg-white rounded-2xl border border-gray-200/80 shadow-xs overflow-hidden">
        {isLoading ? (
          <div className="flex flex-col items-center justify-center p-12">
            <div className="w-8 h-8 border-2 border-indigo-600 border-t-transparent rounded-full animate-spin"></div>
            <p className="text-xs text-gray-500 mt-3 font-medium">Carregando usuários do PostgreSQL...</p>
          </div>
        ) : isError ? (
          <div className="p-8 text-center">
            <AlertTriangle size={32} className="mx-auto text-rose-500 mb-2" />
            <p className="text-sm font-semibold text-gray-800">Falha ao carregar usuários</p>
            <button
              onClick={() => refetch()}
              className="mt-3 px-3 py-1.5 bg-indigo-50 text-indigo-600 text-xs font-medium rounded-lg hover:bg-indigo-100 transition-all"
            >
              Tentar Novamente
            </button>
          </div>
        ) : users.length === 0 ? (
          <div className="p-12 text-center">
            <Users size={40} className="mx-auto text-gray-300 mb-3" />
            <p className="text-sm font-semibold text-gray-700">Nenhum usuário encontrado</p>
            <p className="text-xs text-gray-400 mt-1">Ajuste os filtros ou crie um novo usuário.</p>
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-left text-xs">
              <thead className="bg-gray-50/75 border-b border-gray-200/80 text-gray-500 font-semibold uppercase tracking-wider text-[10px]">
                <tr>
                  <th className="py-3 px-4">Usuário</th>
                  <th className="py-3 px-4">Organização</th>
                  <th className="py-3 px-4">Papel / Nível</th>
                  <th className="py-3 px-4">Plano</th>
                  <th className="py-3 px-4">Status</th>
                  <th className="py-3 px-4">Criado em</th>
                  <th className="py-3 px-4 text-right">Ações</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {users.map((u) => (
                  <tr key={u.id} className="hover:bg-gray-50/50 transition-colors">
                    <td className="py-3.5 px-4 font-medium text-gray-900">
                      <div className="flex items-center gap-2.5">
                        <div className="w-8 h-8 rounded-full bg-indigo-50 text-indigo-600 flex items-center justify-center font-bold text-xs">
                          {u.displayName?.charAt(0) || u.email?.charAt(0) || 'U'}
                        </div>
                        <div>
                          <div className="flex items-center gap-1.5">
                            <p className="font-semibold text-gray-900">{u.displayName || u.fullName}</p>
                            {u.isPlatformAdmin && (
                              <ShieldCheck size={13} className="text-indigo-600" aria-label="Super Admin" />
                            )}
                          </div>
                          <span className="text-[10px] text-gray-400">{u.email}</span>
                        </div>
                      </div>
                    </td>
                    <td className="py-3.5 px-4 text-gray-600 font-medium">
                      {u.organizationName || 'Sem organização'}
                    </td>
                    <td className="py-3.5 px-4">
                      <span className={`inline-flex px-2 py-0.5 rounded-full text-[10px] font-bold ${
                        u.isPlatformAdmin
                          ? 'bg-indigo-50 text-indigo-700 border border-indigo-200'
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        {u.isPlatformAdmin ? 'SUPER_ADMIN' : u.role || 'Membro'}
                      </span>
                    </td>
                    <td className="py-3.5 px-4">
                      <span className="inline-flex px-2 py-0.5 bg-gray-100 text-gray-800 rounded-md text-[10px] font-semibold">
                        {u.currentPlan || 'Pro'}
                      </span>
                    </td>
                    <td className="py-3.5 px-4">
                      <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold ${
                        u.isActive ? 'bg-emerald-50 text-emerald-700' : 'bg-rose-50 text-rose-700'
                      }`}>
                        <span className={`w-1.5 h-1.5 rounded-full ${u.isActive ? 'bg-emerald-500' : 'bg-rose-500'}`} />
                        {u.isActive ? 'Ativo' : 'Inativo'}
                      </span>
                    </td>
                    <td className="py-3.5 px-4 text-gray-500 text-[11px]">
                      {new Date(u.createdAt).toLocaleDateString('pt-BR')}
                    </td>
                    <td className="py-3.5 px-4 text-right">
                      <div className="flex items-center justify-end gap-1">
                        <button
                          onClick={() => handleCopyUid(u.uid)}
                          title="Copiar ID"
                          className="p-1.5 text-gray-400 hover:text-gray-700 rounded-lg hover:bg-gray-100 transition-all"
                        >
                          {copiedUid === u.uid ? <Check size={13} className="text-emerald-600" /> : <Copy size={13} />}
                        </button>
                        <button
                          onClick={() => resetPasswordMutation.mutate(u.id)}
                          title="Redefinir Senha"
                          className="p-1.5 text-gray-400 hover:text-amber-600 rounded-lg hover:bg-gray-100 transition-all"
                        >
                          <Lock size={13} />
                        </button>
                        <button
                          onClick={() => toggleActiveMutation.mutate(u.id)}
                          title={u.isActive ? 'Desativar Usuário' : 'Ativar Usuário'}
                          className="p-1.5 text-gray-400 hover:text-indigo-600 rounded-lg hover:bg-gray-100 transition-all"
                        >
                          <Power size={13} />
                        </button>
                        <button
                          onClick={() => handleOpenEdit(u)}
                          title="Editar Usuário"
                          className="p-1.5 text-gray-400 hover:text-indigo-600 rounded-lg hover:bg-gray-100 transition-all"
                        >
                          <Edit3 size={13} />
                        </button>
                        <button
                          onClick={() => handleDelete(u)}
                          title="Remover Usuário"
                          className="p-1.5 text-gray-400 hover:text-rose-600 rounded-lg hover:bg-gray-100 transition-all"
                        >
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Create / Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 backdrop-blur-xs p-4">
          <div className="bg-white rounded-2xl max-w-md w-full p-6 shadow-2xl border border-gray-100 animate-in zoom-in-95 duration-200">
            <h2 className="text-lg font-bold text-gray-900 mb-1">
              {isCreateMode ? 'Criar Novo Usuário' : 'Editar Usuário'}
            </h2>
            <p className="text-xs text-gray-500 mb-4">
              Configure as credenciais e o nível de acesso administrativo.
            </p>

            <form onSubmit={handleSubmit} className="space-y-3.5">
              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Nome Completo</label>
                <input
                  type="text"
                  required
                  value={formData.fullName}
                  onChange={(e) => setFormData({ ...formData, fullName: e.target.value, displayName: e.target.value })}
                  placeholder="Ex: João da Silva"
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs focus:outline-hidden focus:bg-white focus:border-indigo-500"
                />
              </div>

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">E-mail</label>
                <input
                  type="email"
                  required
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  placeholder="joao@empresa.com"
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs focus:outline-hidden focus:bg-white focus:border-indigo-500"
                />
              </div>

              {isCreateMode && (
                <div>
                  <label className="block text-xs font-semibold text-gray-700 mb-1">Senha (Opcional - Gerada automaticamente se vazia)</label>
                  <input
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                    placeholder="Deixe em branco para gerar aleatória"
                    className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs focus:outline-hidden focus:bg-white focus:border-indigo-500"
                  />
                </div>
              )}

              <div>
                <label className="block text-xs font-semibold text-gray-700 mb-1">Organização</label>
                <select
                  value={formData.organizationId}
                  onChange={(e) => setFormData({ ...formData, organizationId: e.target.value })}
                  className="w-full px-3 py-2 bg-gray-50 border border-gray-200 rounded-xl text-xs font-medium text-gray-700 focus:outline-hidden"
                >
                  <option value="">Sem organização associada</option>
                  {tenants.map((t) => (
                    <option key={t.id} value={t.id}>
                      {t.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="pt-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.isPlatformAdmin}
                    onChange={(e) => setFormData({ ...formData, isPlatformAdmin: e.target.checked })}
                    className="rounded-md border-gray-300 text-indigo-600 focus:ring-indigo-500 h-4 w-4"
                  />
                  <div>
                    <p className="text-xs font-semibold text-gray-900">Acesso SUPER_ADMIN</p>
                    <p className="text-[10px] text-gray-500">Permite visualizar painéis administrativos globais</p>
                  </div>
                </label>
              </div>

              <div className="flex items-center justify-end gap-2 pt-4 border-t border-gray-100">
                <button
                  type="button"
                  onClick={() => setIsModalOpen(false)}
                  className="px-4 py-2 text-xs font-medium text-gray-600 hover:bg-gray-100 rounded-xl transition-all"
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  disabled={createMutation.isPending || updateMutation.isPending}
                  className="px-4 py-2 text-xs font-semibold text-white bg-indigo-600 hover:bg-indigo-700 rounded-xl transition-all disabled:opacity-50"
                >
                  {createMutation.isPending || updateMutation.isPending ? 'Salvando...' : 'Salvar Usuário'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
}
